import { useMemo } from "react";
import { cx } from "../../../components/ui";
import {
  AWAL, AKHIR, HARI_PANJANG, STATUS_LABEL, blokTrainer, hariTeks, inisial, jamTeks, labelBlok,
  posisi, rentangTeks, statusTrainer
} from "./logika";

// ─── Linimasa jam mengajar: satu baris per trainer, 08.00–22.00 ──────────────
//
// Garis jam dan pita sorotan jam terpilih digambar SEKALI untuk semua baris, bukan per
// baris: 80 trainer × 15 garis = seribuan elemen yang tak membawa informasi apa pun.
// Pita itu juga sengaja berada di LUAR daftar ber-key, supaya saat jam diganti ia
// menggeser (transition left/width) alih-alih muncul ulang.

const KIRI = 280;   // px — kolom nama
const JAM = Array.from({ length: (AKHIR - AWAL) / 60 + 1 }, (_, i) => AWAL + i * 60);
const PERIODE = [["Pagi", 8, 12], ["Siang", 12, 16], ["Sore", 16, 18], ["Malam", 18, 22]];
const TEBAL = new Set([12 * 60, 16 * 60, 18 * 60]);
// Undakan blok per baris dibatasi 12 baris × 25ms: baris ke-80 tak boleh menunggu 2 detik.
const TUNDA_MAKS = 12;

const persen = (m) => ((m - AWAL) / (AKHIR - AWAL)) * 100;
// Diukur terhadap SELURUH lebar (kolom nama + jalur jam).
const kiriPenuh = (p) => `calc(${KIRI}px + (100% - ${KIRI}px) * ${p / 100})`;
const lebarPenuh = (p) => `calc((100% - ${KIRI}px) * ${p / 100})`;

const BLOK_BIRU = "bg-primary text-white dark:bg-[#2563eb]";
const BLOK_KUNING = "border border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-300";
const BLOK_ABU = "bg-[#dbe2ea] text-[#334155] dark:bg-[#2a3547] dark:text-[#cbd5e1]";
export const PIL_STATUS = {
  kosong: "bg-primary/10 text-primary ring-1 ring-primary/30 dark:text-[#3987e5]",
  "belum-pasti": "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300",
  "ada-kelas": "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
};

const hariKelas = (k) => (k.hariDiisi ? k.hari.map((d) => HARI_PANJANG[d]).join(", ") : "hari belum diisi");

export function ringkasKelas(t) {
  if (!t.kelas.length) return "Belum punya kelas tetap";
  return `${t.kelas.length} kelas: ${t.kelas.map((k) => `${k.mulai == null ? k.sesi : rentangTeks(k.mulai, k.selesai)} di ${k.cabang}, ${hariKelas(k)}`).join("; ")}`;
}

export default function Linimasa({ trainers, sesi, hari, onPilih, labelBulan }) {
  const pita = sesi ? posisi(sesi.mulai, sesi.selesai) : null;
  // Daftar masuk ulang setiap URUTANNYA berubah (urutan pilihan, jam, hari, penyaring).
  const kunciUrutan = trainers.map((t) => t.id).join(",");

  return (
    <div className="relative min-w-[1100px]">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        {JAM.map((m) => (
          <div
            key={m}
            className={cx("absolute inset-y-0 w-px",
              TEBAL.has(m) ? "bg-slate-300 dark:bg-slate-600" : "bg-slate-100 dark:bg-slate-800")}
            style={{ left: kiriPenuh(persen(m)) }}
          />
        ))}
        {pita && (
          <div
            data-testid="pita-jam"
            className="anim-fade geser-posisi absolute inset-y-0 bg-primary/10 dark:bg-primary/20"
            style={{ left: kiriPenuh(pita.left), width: lebarPenuh(pita.width) }}
          />
        )}
      </div>

      <div className="relative grid grid-cols-[280px_1fr] border-b border-slate-200 dark:border-slate-800">
        <div className="px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-500">Trainer</div>
        <div aria-hidden="true" className="relative h-12">
          {PERIODE.map(([nama, a, b]) => (
            <span
              key={nama}
              className="absolute top-1.5 pl-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400"
              style={{ left: `${persen(a * 60)}%`, width: `${persen(b * 60) - persen(a * 60)}%` }}
            >
              {nama}
            </span>
          ))}
          {JAM.map((m, i) => (
            <span
              key={m}
              className={cx("absolute bottom-1.5 text-[10px] tabular-nums text-slate-400",
                i === 0 ? "pl-1" : i === JAM.length - 1 ? "-translate-x-full pr-1" : "-translate-x-1/2")}
              style={{ left: `${persen(m)}%` }}
            >
              {jamTeks(m)}
            </span>
          ))}
        </div>
      </div>

      <div key={kunciUrutan} className="anim-fade relative">
        {trainers.map((t, i) => (
          <Baris key={t.id} t={t} urutan={i} sesi={sesi} hari={hari} onPilih={onPilih} labelBulan={labelBulan} />
        ))}
      </div>
    </div>
  );
}

function Baris({ t, urutan, sesi, hari, onPilih, labelBulan }) {
  const { blok, lajur, takTerbaca } = useMemo(() => blokTrainer(t), [t]);
  const status = sesi ? statusTrainer(t, sesi.sesi, hari) : null;
  const tunda = `${Math.min(urutan, TUNDA_MAKS) * 25}ms`;
  const kosong = sesi && status === "kosong" ? posisi(sesi.mulai, sesi.selesai) : null;
  const b = t.bulanIni;

  const label = [
    t.nama,
    t.kode && `kode ${t.kode}`,
    t.cabang.length ? `cabang ${t.cabang.join(" dan ")}` : "",
    t.namaSama && "nama panggilannya sama dengan akun lain"
  ].filter(Boolean).join(", ")
    + ". "
    + (sesi ? `Jam ${rentangTeks(sesi.mulai, sesi.selesai)}, ${hariTeks(hari)}: ${STATUS_LABEL[status]}. ` : "")
    + `${ringkasKelas(t)}. `
    + `${labelBulan}: kelasnya diganti ${b.diganti.kali} kali, mengganti trainer lain ${b.mengganti.kali} kali. Buka rincian.`;

  return (
    <button
      type="button"
      onClick={() => onPilih(t.id)}
      aria-label={label}
      className="group grid w-full grid-cols-[280px_1fr] border-b border-slate-100 text-left transition-colors hover:bg-slate-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:border-slate-800 dark:hover:bg-slate-800/30"
    >
      <span className="flex min-w-0 items-center gap-3 px-4 py-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-black text-primary transition-transform duration-200 group-hover:scale-110 dark:bg-primary/20 dark:text-[#3987e5]">
          {inisial(t.nama)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-black text-slate-900 dark:text-white">{t.nama}</span>
            {t.namaSama && (
              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-700 dark:text-amber-300">
                Nama sama
              </span>
            )}
            {status && (
              <span className={cx("rounded-full px-2 py-0.5 text-[10px] font-black", PIL_STATUS[status])}>
                {STATUS_LABEL[status]}
              </span>
            )}
          </span>
          <span className="block truncate text-xs text-slate-500">
            {[t.kode, t.cabang.join(", ")].filter(Boolean).join(" · ") || "—"}
          </span>
          <span className="block truncate text-[11px] text-slate-400">
            {t.kelas.length} kelas · diganti {b.diganti.kali}× · mengganti {b.mengganti.kali}×
          </span>
        </span>
      </span>

      <span className="relative my-2 block h-12">
        {blok.map((x) => {
          const p = posisi(x.mulai, x.selesai);
          const tinggi = 100 / lajur;
          const cabang = `${x.cabang.join(" · ")}${x.adaTanpaHari ? " · hari?" : ""}`;
          return (
            <span
              key={x.kunci}
              data-testid="blok"
              title={x.kelas.map((k) => `${rentangTeks(x.mulai, x.selesai)} · ${k.nama} (${k.cabang}) · ${hariKelas(k)}`).join("\n")}
              className={cx(
                "anim-grow-x absolute flex flex-col justify-center overflow-hidden rounded-md px-1.5 text-[11px] font-bold leading-tight",
                sesi ? BLOK_ABU : x.tanpaHari ? BLOK_KUNING : BLOK_BIRU,
                sesi && x.adaTanpaHari && "border border-dashed border-amber-500/70"
              )}
              style={{
                left: `${p.left}%`,
                width: `${p.width}%`,
                top: `calc(${x.lajur * tinggi}% + 1px)`,
                height: `calc(${tinggi}% - 2px)`,
                animationDelay: tunda
              }}
            >
              {lajur > 1 ? (
                // Setengah tinggi: satu baris saja, jadi jam mulai + cabang — rentang penuhnya terpotong.
                <span className="truncate">{jamTeks(x.mulai)} · {cabang}</span>
              ) : (
                <>
                  <span className="truncate">{labelBlok(x.mulai, x.selesai)}</span>
                  <span className="truncate font-semibold opacity-80">{cabang}</span>
                </>
              )}
            </span>
          );
        })}

        {kosong && (
          <span
            data-testid="jam-kosong"
            className="anim-pop-sm geser-posisi absolute inset-y-0 z-10 flex items-center justify-center overflow-hidden rounded-md border-2 border-primary bg-white/95 px-1 text-[11px] font-black text-primary dark:border-[#3987e5] dark:bg-slate-900/95 dark:text-[#3987e5]"
            style={{ left: `${kosong.left}%`, width: `${kosong.width}%` }}
          >
            <span className="truncate">Jam kosong</span>
          </span>
        )}

        {!t.kelas.length && (
          <span className="absolute inset-y-0 left-2 flex items-center text-xs text-slate-400">
            Belum punya kelas tetap · hanya mengisi kelas trainer lain
          </span>
        )}

        {takTerbaca.length > 0 && (
          <span className="absolute right-1 top-0 rounded bg-amber-500/15 px-1.5 text-[10px] font-black text-amber-700 dark:text-amber-300">
            +{takTerbaca.length} kelas jamnya tak terbaca
          </span>
        )}
      </span>
    </button>
  );
}
